/**
 * Archive search + category filter.
 *
 * Filtering is done SERVER-SIDE: the controls write `category` and `q` into the
 * query string and reload, and the archive template applies them to the
 * collection query (see articles-archive.antlers.html).
 *
 * This is deliberate. The listing paginates at 9 per page, so filtering the
 * rendered cards in JS would only ever search the current page and silently
 * hide matches on the others. Going through the server also makes the filtered
 * view a real, shareable URL:
 *
 *   /resources/articles?category=immersive-learning,ai-in-learning&q=pressure
 *
 * This script therefore only manages the UI — opening the menu, the count
 * badge, and navigating on change. With JS off the checkboxes do nothing, but
 * the cards' own category pills are plain links carrying `?category=`, so there
 * is still a non-JS route to a filtered listing.
 *
 * No-ops when no [data-archive-filter] toolbar is present.
 */
function initArchiveFilter() {
  document.querySelectorAll("[data-archive-filter]").forEach((toolbar) => {
    if (toolbar.hasAttribute("data-archive-filter-initialized")) return;
    toolbar.setAttribute("data-archive-filter-initialized", "");

    const searchInput = toolbar.querySelector("[data-archive-search]");
    const dropdown = toolbar.querySelector("[data-archive-dropdown]");
    const toggle = toolbar.querySelector("[data-archive-dropdown-toggle]");
    const menu = toolbar.querySelector("[data-archive-dropdown-menu]");
    const count = toolbar.querySelector("[data-archive-count]");
    const checkboxes = Array.from(
      toolbar.querySelectorAll("[data-archive-term]"),
    );

    const params = new URLSearchParams(window.location.search);

    // --- Restore state from the URL ------------------------------------
    // The server has already filtered the results; this just makes the controls
    // agree with what is on screen.
    const activeTerms = (params.get("category") || "")
      .split(",")
      .map((slug) => slug.trim())
      .filter(Boolean);

    checkboxes.forEach((box) => {
      box.checked = activeTerms.includes(box.value);
    });

    if (searchInput) searchInput.value = params.get("q") || "";

    const updateBadge = () => {
      const n = checkboxes.filter((box) => box.checked).length;
      if (count) {
        count.textContent = n ? String(n) : "";
        count.hidden = n === 0;
      }
      if (dropdown) dropdown.classList.toggle("has-active", n > 0);
    };

    updateBadge();

    // --- Navigate ------------------------------------------------------
    const submit = () => {
      const next = new URLSearchParams(window.location.search);

      const active = checkboxes
        .filter((box) => box.checked)
        .map((box) => box.value);

      if (active.length) {
        next.set("category", active.join(","));
      } else {
        next.delete("category");
      }

      const query = searchInput ? searchInput.value.trim() : "";
      if (query) {
        next.set("q", query);
      } else {
        next.delete("q");
      }

      // Any change puts the reader back on page 1 — staying on page 4 of a
      // filter that now has one page of results would land on an empty grid.
      next.delete("page");

      const search = next.toString();
      window.location.assign(
        search ? `${window.location.pathname}?${search}` : window.location.pathname,
      );
    };

    // --- Search --------------------------------------------------------
    if (searchInput) {
      // Debounced: this navigates, so firing per keystroke would reload the
      // page mid-word.
      let timer;
      const scheduleSubmit = () => {
        clearTimeout(timer);
        timer = setTimeout(submit, 600);
      };

      searchInput.addEventListener("input", scheduleSubmit);

      // Enter submits immediately rather than waiting out the debounce.
      searchInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          clearTimeout(timer);
          submit();
        }
      });
    }

    // --- Category dropdown ---------------------------------------------
    if (dropdown && toggle && menu) {
      const closeMenu = () => {
        menu.hidden = true;
        toggle.setAttribute("aria-expanded", "false");
      };

      toggle.addEventListener("click", () => {
        const open = toggle.getAttribute("aria-expanded") === "true";
        menu.hidden = open;
        toggle.setAttribute("aria-expanded", String(!open));
      });

      document.addEventListener("click", (event) => {
        if (!dropdown.contains(event.target)) closeMenu();
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") closeMenu();
      });

      // The badge updates instantly so the tick feels responsive; the reload
      // follows on a short delay, which also lets a reader tick two boxes
      // without triggering two navigations.
      let changeTimer;
      checkboxes.forEach((box) => {
        box.addEventListener("change", () => {
          updateBadge();
          clearTimeout(changeTimer);
          changeTimer = setTimeout(submit, 400);
        });
      });
    }
  });
}

export { initArchiveFilter };
